import React from "react";

import { Dropdown } from "~/components/dropdown";
import { useGridLayoutContext } from "~/components/grid-layout/use-grid-layout-context";
import { IconExternalLink } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { pluralize } from "~/tools/pluralize";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import type { DashboardWidgetLink } from "../../common/dashboard-spec";
import { Link, LinkTo } from "../react/link";

const pluralizeLink = pluralize({
	1: "1 link",
	more: (count) => `${count} links`,
});

export interface DashboardWidgetLinksProps {
	primaryLink: DashboardWidgetLink;
	otherLinks: ReadonlyArray<DashboardWidgetLink>;
}

export const DashboardWidgetLinks: React.FC<DashboardWidgetLinksProps> = ({
	primaryLink,
	otherLinks,
}: DashboardWidgetLinksProps) => {
	const gridLayoutContext = useGridLayoutContext();

	return (
		<div className="flex gap-1.5 text-13/[21px] text-gray-600">
			<WidgetLink {...primaryLink} className={cx("min-w-0", gridLayoutContext?.dragCancelClassName)} />

			{otherLinks.length > 0 && <span>+</span>}

			{otherLinks.length > 0 && (
				<Dropdown
					destroyPopupOnHide
					overlay={
						<ol className="max-w-[220px] space-y-3 overflow-hidden rounded-sm border border-gray-300 border-solid bg-white p-4 text-13/none text-gray-600 shadow-xs">
							{otherLinks.map((link, index) => (
								<li key={index}>
									<WidgetLink {...link} />
								</li>
							))}
						</ol>
					}
					placement="bottomLeft"
					trigger={["click"]}
				>
					<Link
						className={cx("text-inherit", gridLayoutContext?.dragCancelClassName)}
						el="button"
						theme="secondary"
						type="button"
					>
						{pluralizeLink(otherLinks.length)}
					</Link>
				</Dropdown>
			)}
		</div>
	);
};

const RELATIVE_LINK_REGEX = /^\/?#/u;

const WidgetLink: React.FC<DashboardWidgetLink & { className?: string }> = ({
	url,
	name,
	tooltip,
	targetBlank,
	className,
}) => {
	const variables = useTemplateVariables();

	const interpolatedUrl = url.interpolate(variables.values) ?? "";
	const interpolatedTooltip = tooltip?.interpolate(variables.values);
	const interpolatedName = name?.interpolate(variables.values);

	const isRelative = RELATIVE_LINK_REGEX.test(interpolatedUrl);

	return (
		<div className="flex min-w-0 items-center gap-1">
			<Truncated
				className={cx("text-inherit", className)}
				el={LinkTo}
				href={isRelative ? undefined : interpolatedUrl}
				search={["timeRange", "timestamp", "detachedFilters"]}
				target={targetBlank ? "_blank" : undefined}
				theme="secondary"
				to={interpolatedUrl.replace(RELATIVE_LINK_REGEX, "")}
				tooltip={interpolatedTooltip}
				truncate="middle"
			>
				{interpolatedName ?? interpolatedUrl}
			</Truncated>

			{targetBlank && <IconExternalLink />}
		</div>
	);
};
